import { useEffect, useState } from "react"
import { NatsService } from "services/nats.service"
import type { Subscription } from "nats.ws"
import type { IProgressMessage, IStatusMessage, IAnswerMessage, TNatsCreds } from "types/nats.type"
import type { IPredefinedQuestionSummaryMessage } from "types/aiAgent.type"
import { useMessagesDataStore } from "store/nats.store"
import { useMessage } from "./message.hook"
import { useBidRequestFormData, useChatStore, useSeriesStore } from "store/aiAgent.store"

const natsService = new NatsService()

export const useNatsProcessing = (
	serverUrl: string,
	progressTopic: string,
	statusTopic: string,
	natsCreds: TNatsCreds,
) => {
	const { setMessagesData } = useMessagesDataStore()
	const { error: userErrorMessage } = useMessage()

	useEffect(() => {
		let progressSub: Subscription | undefined
		let statusSub: Subscription | undefined

		const connectAndSubscribe = async () => {
			try {
				await natsService.connect(serverUrl, natsCreds)

				progressSub = natsService.subscribe<IProgressMessage>(progressTopic, (message, subject) => {
					const id = subject.split(".")[3]
					const idAsNumber = parseInt(id, 10)
					setMessagesData(idAsNumber, { progress: message.progress })
				})

				statusSub = natsService.subscribe<IStatusMessage>(statusTopic, (message, subject) => {
					const id = subject.split(".")[3]
					const idAsNumber = parseInt(id, 10)
					setMessagesData(idAsNumber, { status: { status: message.status } })
				})
			} catch (e) {
				// Show error message to user
				const error = e as Error
				if (error) userErrorMessage("Something went wrong, please try again later")
			}
		}

		// Connect to NATS if credentials are provided
		if (natsCreds) {
			connectAndSubscribe()
		}

		return () => {
			// Clean up: unsubscribe and disconnect when the component unmounts
			progressSub?.unsubscribe()
			statusSub?.unsubscribe()
			natsService.close().catch((e) => console.error("Error closing NATS connection:", e))
		}
	}, [serverUrl, progressTopic, statusTopic, setMessagesData, userErrorMessage, natsCreds])
}

export const useNatsAnswerStream = (serverUrl: string, topic: string, natsCreds: TNatsCreds) => {
	const [answer, setAnswer] = useState<IAnswerMessage | undefined>()
	const { error: userErrorMessage } = useMessage()
	const { addAnswer, setFailedAnswer, markLastAnswerForSeriesSelector, markLastAnswerForBidRequestForm } =
		useChatStore()
	const { setActiveSeries } = useSeriesStore()
	const { setActiveBidRequest } = useBidRequestFormData()

	useEffect(() => {
		let answerSub: Subscription | undefined

		const connectAndSubscribe = async () => {
			try {
				await natsService.connect(serverUrl, natsCreds)

				answerSub = natsService.subscribe<IAnswerMessage>(topic, (message) => {
					setAnswer(message)

					const messageContent = message.message

					if (message.status === "PREDEFINED_QUESTION_SUMMARY") {
						// Type assertion since we know this message contains series data when status is PREDEFINED_QUESTION_SUMMARY
						const messageWithSeries = messageContent as unknown as IPredefinedQuestionSummaryMessage

						// Set the series to store and mark answer for display when question_type is series_extraction
						if (messageWithSeries.question_type === "series_extraction") {
							const series = {
								availableSeries: messageWithSeries.available_series,
								extractedSeries: messageWithSeries.extracted_series,
							}
							setActiveSeries(series)
							markLastAnswerForSeriesSelector()
						}

						// Set the bid request form data to store and mark answer for display
						if (messageWithSeries.question_type === "bid_request") {
							const bidrequestFormData = {
								extractedFields: messageWithSeries.extracted_fields || {},
								availableFields: messageWithSeries.form_schema || {},
							}
							setActiveBidRequest(bidrequestFormData)
							markLastAnswerForBidRequestForm()
						}
					}

					if (message.status === "FAILED") {
						setFailedAnswer()
					} else {
						// Only add answer if message.message is defined and not null and status is RUNNING or PREDEFINED_QUESTION_SUMMARY
						if (messageContent !== undefined && messageContent !== null && message.status === "RUNNING") {
							addAnswer(messageContent)
						}
					}
				})
			} catch (e) {
				const error = e as Error
				if (error) userErrorMessage("Something went wrong, please try again later")
			}
		}

		// Connect to NATS if topic and credentials are provided
		if (topic && natsCreds) {
			connectAndSubscribe()
		}

		return () => {
			answerSub?.unsubscribe()
			natsService.close().catch((e) => console.error("Error closing NATS connection:", e))
		}
	}, [
		addAnswer,
		setFailedAnswer,
		serverUrl,
		topic,
		userErrorMessage,
		natsCreds,
		setActiveSeries,
		markLastAnswerForSeriesSelector,
		setActiveBidRequest,
		markLastAnswerForBidRequestForm,
	])

	return answer
}
